import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientMutation } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const route = 'SUBUSER_IMPORT';

// Initial state
const initialState = {
  openImport: false,
  loading: false,
  importResponse: null,
  error: null,
  fileKey: null,
  delimiter: '',
  importNotification: null,
};

// Async thunk for importing subUsers
export const importSubUsers = createAsyncThunk(
  `${route}/importSubUsers`,
  async (payload, { rejectWithValue }) => {
    try {
      const { fileKey, delimiter } = payload;

      const query = `
        mutation importUsers ($fileKey: String!, $delimiter: String!) {
          importUsers (fileKey: $fileKey, delimiter: $delimiter) {
            stepFunctionId
            transactionId
            transactionType
            processStartDate
          }
        }
      `;

      const response = await clientMutation(
        query,
        {
          fileKey,
          delimiter,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (!response || !response.importUsers) {
        throw new Error('Error while importing subUsers', response.error);
      }

      return response.importUsers;
    } catch (exc) {
      ExceptionManager(
        exc,
        'modules/account/views/subUser/components/importSubUserModal/redux/slice',
        'importSubUsers'
      );
      return rejectWithValue(exc.error);
    }
  }
);

// Slice
const subUsersImportSlice = createSlice({
  name: route,
  initialState,
  reducers: {
    // Open/close modal actions
    openImportSubUserModal: (state, action) => {
      state.openImport = action.payload;
      if (!action.payload) {
        // Reset state when closing modal
        state.importResponse = null;
        state.error = null;
        state.fileKey = null;
        state.importNotification = null;
      }
    },
    
    // Set file key for import
    setFileKey: (state, action) => {
      state.fileKey = action.payload;
    },
    
    // Set delimiter
    setDelimiter: (state, action) => {
      state.delimiter = action.payload;
    },
    
    // Reset import state
    resetImportState: (state) => {
      state.loading = false;
      state.importResponse = null;
      state.error = null;
      state.fileKey = null;
      state.delimiter = ',';
      state.importNotification = null;
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    
    // Set import notification
    setImportNotificationSubUser: (state, action) => {
      // Add validation for notification payload structure
      const notification = action.payload;
      if (notification && typeof notification === 'object') {
        state.importNotification = notification;
      } else {
        state.importNotification = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Import subUsers pending
      .addCase(importSubUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importResponse = null;
      })
      // Import subUsers fulfilled
      .addCase(importSubUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.importResponse = action.payload;
        state.error = null;
      })
      // Import subUsers rejected
      .addCase(importSubUsers.rejected, (state, action) => {
        state.loading = false;
        state.importResponse = null;
        state.error = action.payload || 'Import failed';
      });
  },
});

// Export actions
export const {
  openImportSubUserModal,
  setFileKey,
  setDelimiter,
  resetImportState,
  clearError,
  setImportNotificationSubUser,
} = subUsersImportSlice.actions;

// Export reducer
export default subUsersImportSlice.reducer;